import { Context } from '../models/context';
import { IName } from '../interfaces/global.interfaces';
import { Order } from '../models/order';
import { IOrderCoupon, IOrderItem, IOrderItemCoupon } from '../models/order.interface';
import { LocalizationService } from '../services/localization-service';

export interface ICouponItemDebugRow {
    Coupon: string,
    CouponKey: number,
    CouponId: number | null,
    CouponAmount: number | null,
    Item: string,
    ItemKey: number,
    Half: number,
    BeforeTax: number,
    AfterTax: number,
    ItemDiscount: number,
    DiscountedQty: number,
    IsActive: boolean,
    Price: number | null,
    MenuPrice: number | null,
    Note: string
}

const PRICE_REDUCER_NOTE = "price reducer – compare Price vs MenuPrice";

/**
 * QA-only console helper: exposes window.oloDebug.coupons() when developmentMode is on.
 * Registers nothing otherwise, so window.oloDebug stays undefined in prod.
 */
export function registerDebugTools(context: Context, order: Order, localizationService: LocalizationService): void {
    if (context.getProperty("developmentMode") !== true)
        return;

    globalThis.oloDebug = {
        coupons: (): ICouponItemDebugRow[] => {
            const rows = buildCouponItemRows(order, localizationService);
            console.table(rows);
            return rows;
        }
    };
}

/** One row per ItemsCoupons entry, joined to its coupon and cart item. Read-only. */
export function buildCouponItemRows(order: Order, localizationService: LocalizationService): ICouponItemDebugRow[] {
    const coupons = order.getProperty("Coupons") ?? [];
    const items = order.getProperty("Items") ?? [];
    const itemsCoupons = order.getProperty("ItemsCoupons") ?? [];

    return itemsCoupons.map(link => {
        const coupon = coupons.find(c => c.CouponKey === link.CouponKey) ?? null;
        const item = items.find(i => i.ItemKey === link.ItemKey && i.HalfIndex === link.HalfIndex) ?? null;
        return buildRow(link, coupon, item, order, localizationService);
    });
}

function buildRow(link: IOrderItemCoupon, coupon: IOrderCoupon | null, item: IOrderItem | null, order: Order, localizationService: LocalizationService): ICouponItemDebugRow {
    const dataCoupon = coupon ? order.couponGetCoupon(coupon.CouponId, false, null, coupon.ChannelID) : null;

    return {
        Coupon: dataCoupon ? localizationService.GetName(dataCoupon.Names) : "",
        CouponKey: link.CouponKey,
        CouponId: coupon?.CouponId ?? null,
        CouponAmount: coupon?.Amount ?? null,
        Item: item ? getItemName(item, localizationService) : "",
        ItemKey: link.ItemKey,
        Half: link.HalfIndex,
        BeforeTax: link.BeforeTaxDiscount,
        AfterTax: link.AfterTaxDiscount,
        ItemDiscount: link.BeforeTaxDiscount + link.AfterTaxDiscount,
        DiscountedQty: link.DiscountedQuantity,
        IsActive: link.IsActive,
        Price: item?.Price ?? null,
        MenuPrice: item?.MenuPrice ?? null,
        Note: dataCoupon?.IsPriceReducer ? PRICE_REDUCER_NOTE : ""
    };
}

// Order item Name is JSON.stringify(Names); fall back to the raw string if it isn't JSON.
function getItemName(item: IOrderItem, localizationService: LocalizationService): string {
    try {
        const names: IName[] = JSON.parse(item.Name);
        return localizationService.GetName(names);
    } catch {
        return item.Name ?? "";
    }
}
